/* eslint-disable no-unused-vars */

import React, { useState, useEffect } from 'react';
import './Incidents.css'; // Import the CSS file
import { 
  Select, MenuItem, InputLabel, FormControl, Checkbox, ListItemText,
  Button, TextField, IconButton // Added TextField, IconButton
} from '@mui/material';
import { AddCircleOutline, RemoveCircleOutline } from '@mui/icons-material'; // Added icons
import FirstAidForm from './forms/FirstAidForm';
import VandalismForm from './forms/VandalismForm';
import SafetyConcernsForm from './forms/SafetyConcernsForm';
import WorkRefusalForm from './forms/WorkRefusalForm';
import NearMissForm from './forms/NearMissForm';
import FatalitiesForm from './forms/FatalitiesForm';
import HospitalAdmittanceForm from './forms/HospitalAdmittanceForm';
import InjuriesLostTimeForm from './forms/InjuriesLostTimeForm';
import InjuriesNonLostTimeForm from './forms/InjuriesNonLostTimeForm';
import OHSSeriousForm from './forms/OHSSeriousForm';
import PSIForm from './forms/PSIForm';
import PropertyEquipmentDamagesForm from './forms/PropertyEquipmentDamagesForm';
import VehicleIncidentForm from './forms/VehicleIncidentForm';
import axios from 'axios';
import API from './services/api'; // Use the centralized API instance
import { getDepartmentDisplayName } from './utils/departmentLabels';
import { getAppPath } from './utils/runtimeMode';

// Create axios instance with auth interceptor (legacy support)
const api = API; // Use the centralized API configuration

// Add interceptor to include token
api.interceptors.request.use(config => {
  const token = localStorage.getItem('authToken');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Get all incidents - with leading slash
export const getIncidents = async () => {
  console.log('Fetching incidents...');
  try {
    const response = await api.get('/api/incidents'); // With leading slash
    console.log('📊 Raw incidents response:', response);
    console.log('📊 Response data:', response.data);
    console.log('📊 Response data type:', typeof response.data);
    if (response.data && response.data.incidents) {
      console.log('📊 Incidents array length:', response.data.incidents.length);
    }
    return response.data;
  } catch (error) {
    console.error('Error fetching incidents:', error);
    throw error;
  }
};

// Create a new incident - with leading slash
export const createIncident = async (incidentData) => {
  console.log('Creating incident...');
  try {
    const response = await api.post('/api/incidents', incidentData);
    
    // Log the full response to see its structure
    console.log('Create incident response:', response);
    
    // If 204 No Content, response.data will be undefined
    if (response.status === 204) {
      return { success: true };
    }
    
    // Ensure we return an object with a clear id property
    if (response.data && typeof response.data === 'object') {
      // Log the data structure
      console.log('Response data structure:', response.data);
      return {
        ...response.data,
        id: response.data.id || response.data.incidentId || response.data.ID,
        incidentId: response.data.incidentId || response.data.id || response.data.ID,
        ID: response.data.ID || response.data.id || response.data.incidentId
      };
    }
    
    return response.data;
  } catch (error) {
    console.error('Error creating incident:', error);
    throw error;
  }
};

const extractIncidentId = (payload) => {
  if (!payload || typeof payload !== 'object') return null;

  return payload.id
    || payload.incidentId
    || payload.ID
    || payload.incident?.id
    || payload.incident?.incidentId
    || payload.incident?.ID
    || null;
};

const getIncidentDashboardLink = (incident = {}) => {
  const incidentId = incident.incident_id || incident.INCIDENT_ID || incident.id;
  return `${window.location.origin}${getAppPath(`/incident-investigations?incidentId=${encodeURIComponent(incidentId || '')}`)}`;
};

// Submit form data - with leading slash
export const submitFormData = async (incidentId, formType, formData) => {
  console.log(`Submitting ${formType} form for incidentId=${incidentId}...`);
  console.log('Form payload:', formData);
  if (!incidentId) {
    const err = new Error('Missing incidentId when submitting form data');
    console.error(err);
    throw err;
  }
  try {
    const response = await api.post(`/api/incidents/${incidentId}/forms`, {
      formType,
      formData
    });
    console.log(`Form submit response for incident ${incidentId}:`, response && response.data);
    return response.data;
  } catch (error) {
    console.error('Error submitting form to server for incident', incidentId, error?.response || error);
    throw error;
  }
};

// Add these diagnostic functions - with NO leading slashes
export const getApiRoutes = async () => {
  try {
    const response = await api.get('/routes'); // With leading slash
    return response.data;
  } catch (error) {
    console.error('Error fetching routes:', error);
    throw error;
  }
};

export const checkDatabaseTables = async () => {
  try {
    const response = await api.get('/check-tables'); // With leading slash
    return response.data;
  } catch (error) {
    console.error('Error checking database tables:', error);
    throw error;
  }
};

export const pingServer = async () => {
  try {
    const response = await api.get('/ping'); // With leading slash
    return response.data;
  } catch (error) {
    console.error('Error pinging server:', error);
    throw error;
  }
};

const sendNotificationToWCBClerk = async (incident) => {
  const wcbClerkEmail = "pgd.hundal@gmail.com";
  const relevantIncidentTypes = [
    "Injuries Non-lost time",
    "Hospital Admittance",
    "Injuries Lost-time",
    "OHS Serious",
    "OHS Potential Serious Incident"
  ];

  // Check if the incident type is relevant
  const shouldNotify = relevantIncidentTypes.includes(incident.title);

  if (shouldNotify) {
    try {
      console.log("Sending notification to WCB clerk...");
      await api.post("/notifications", {
        to: wcbClerkEmail,
        subject: "Incident Notification",
        message: `An incident of type "${incident.title}" has been reported and requires your attention.`,
      });
      console.log("Notification sent to WCB clerk successfully.");
    } catch (error) {
      console.error("Error sending notification to WCB clerk:", error);
    }
  }
};

const sendWCBAndOHSNotifications = async (incident) => {
  console.log('sendWCBAndOHSNotifications called with:', incident);

  // WCB Notification
  const wcbRequired =
    incident.wcbReportRequired === "Yes" ||
    incident.wcbReportRequired === true ||
    incident.WCB_REPORT_REQUIRED === "Yes" ||
    incident.WCB_REPORT_REQUIRED === true;
  if (wcbRequired) {
    try {
      console.log('About to send WCB notification...');
      console.log('About to POST to notifications/send...');
      await api.post('/notifications/send', {
        eventType: 'wcbNotification',
        data: {
          incidentId: incident.id,
          incidentType: incident.title,
          lostDays: incident.lostDays,
          incidentDate: incident.date,
          location: incident.location,
          severity: incident.severity,
          dashboardLink: getIncidentDashboardLink(incident),
          wcbReportRequired:
            incident.wcbReportRequired === "Yes" ||
            incident.WCB_REPORT_REQUIRED === "Yes"
              ? "Yes"
              : "No",
          forwardToWcbClerk:
            incident.forwardToWCB === "Yes" ||
            incident.forwardToWcbClerk === "Yes" ||
            incident.forwardToWcb === "Yes" ||
            incident.FORWARD_TO_WCB === "Yes"
              ? "Yes"
              : "No",
          primaryDepartment: incident.primaryDepartment,
        }
      });
      console.log('POST to notifications/send completed!');
    } catch (error) {
      console.error('Error in notifications/send:', error);
    }
  }

  // OHS Notification
  const ohsRequired =
    incident.ohsReportRequired === "Yes" ||
    incident.OHS_REPORT_REQUIRED === "Yes";
  if (ohsRequired) {
    try {
      await api.post('/notifications/send', {
        eventType: 'ohsReporting',
        data: {
          incidentId: incident.id,
          incidentType: incident.title,
          incidentDate: incident.date,
          location: incident.location,
          severity: incident.severity,
          dashboardLink: getIncidentDashboardLink(incident),
          ohsReportRequired:
            incident.ohsReportRequired === "Yes" ||
            incident.OHS_REPORT_REQUIRED === "Yes"
              ? "Yes"
              : "No",
          primaryDepartment: incident.primaryDepartment,
        }
      });
      console.log('OHS notification sent successfully.');
    } catch (error) {
      console.error('Error sending OHS notification:', error);
    }
  }
};

function getDefaultFormData() {
  return {
    title: '',
    location: '',
    severity: 'Medium',
    description: '',
    date: new Date().toISOString().split('T')[0],
    time: new Date().toTimeString().slice(0, 5),
    completedByInjuredParty: 'No',
    primaryDepartment: '',
    employees: '',
    contractors: '',
    wcbReportRequired: "false",
    wcbClaimNumber: '',
    wcbReportSubmittedDate: '',
    ohsReportRequired: "false",
    ohsReportNumber: '',
    ohsReportSubmittedDate: '',
    forwardToWCB: 'No',
    lostDays: 0,
    // First Aid specific fields
    treatmentProvided: '',
    bodyPartAffected: '',
    injuryType: '',
    medicalAttentionRequired: 'No',
    // Near Miss specific fields
    nearMissDate: '',
    nearMissTime: '',
    actionsTaken: '',
    supervisor: '',
    // Work Refusal specific fields
    issueDescription: '',
    investigationCause: '',
    supervisorNotified: 'No',
    supervisorName: '',
    resolutionSteps: '',
    hazardMitigated: 'No',
    // Vandalism specific fields
    damageType: '',
    estimatedCost: '',
    itemsDamaged: '',
    suspectedPerpetrator: '',
    reportedToPolice: 'No',
    policeReportNumber: '',
  // Property/Equipment Damages specific fields
  equipmentInvolved: 'No',
  equipmentOther: '',
  propertyInvolved: 'No',
  propertyDescription: '',
  over5000: 'No',
  reportedToPoliceDamage: 'No',
  policeReportDate: '',
  under5000: 'No',
  estimatedDamages: ''
  ,
  // Vehicle Incident specific fields
  vehicleType: '',
  vehicleOther: '',
  thirdPartyInvolved: 'No',
  thirdPartyName: '',
  thirdPartyLicensePlate: '',
  thirdPartyInsurance: '',
  reportedToPoliceVehicle: 'No',
  policeReportDateVehicle: ''
  };
}

const INCIDENT_DRAFTS_STORAGE_KEY = 'incidentDrafts';

const normalizeDraftEmail = (value) => String(value || '').trim().toLowerCase();

const getCurrentDraftUserEmail = () => {
  try {
    const storedUser = localStorage.getItem('user');
    if (storedUser) {
      const parsedUser = JSON.parse(storedUser);
      const parsedEmail = normalizeDraftEmail(
        parsedUser?.username || parsedUser?.email || parsedUser?.EMAIL
      );
      if (parsedEmail) {
        return parsedEmail;
      }
    }
  } catch (error) {
    console.error('Error reading current user for incident drafts:', error);
  }

  return normalizeDraftEmail(localStorage.getItem('userEmail'));
};

const getDraftSubjectEmail = (draftFormData = {}) => {
  const isOnBehalfOfEmployee = String(draftFormData.completedByInjuredParty || '').trim().toLowerCase() === 'yes';
  if (!isOnBehalfOfEmployee) {
    return '';
  }

  return normalizeDraftEmail(
    draftFormData.employees || draftFormData.employeeEmail || draftFormData.employeeName
  );
};

const hydrateIncidentDraft = (draft = {}) => {
  const subjectEmail = normalizeDraftEmail(draft.createdForEmail) || getDraftSubjectEmail(draft.formData);
  const creatorEmail = normalizeDraftEmail(draft.createdByEmail || draft.ownerEmail);
  const visibleToEmails = Array.from(
    new Set(
      [
        ...(Array.isArray(draft.visibleToEmails) ? draft.visibleToEmails : []),
        creatorEmail,
        subjectEmail
      ]
        .map(normalizeDraftEmail)
        .filter(Boolean)
    )
  );

  return {
    ...draft,
    createdByEmail: creatorEmail,
    createdForEmail: subjectEmail || creatorEmail,
    visibleToEmails
  };
};

const canUserAccessIncidentDraft = (draft, userEmail) => {
  const normalizedUserEmail = normalizeDraftEmail(userEmail);
  if (!normalizedUserEmail) {
    return false;
  }

  const hydratedDraft = hydrateIncidentDraft(draft);
  return hydratedDraft.visibleToEmails.includes(normalizedUserEmail);
};

const normalizeEmployeeList = (raw = []) => raw.map(u => ({
  id: u.id || u.ID || u.username || u.USERNAME || u.email || `${(u.firstName||u.FIRST_NAME||'').trim()}_${(u.lastName||u.LAST_NAME||'').trim()}`,
  email: u.email || u.EMAIL || u.user || u.USER || '',
  firstName: (u.firstName || u.first_name || u.FIRST_NAME || '').trim(),
  lastName: (u.lastName || u.last_name || u.LAST_NAME || '').trim(),
  jobTitle: (u.jobTitle || u.TITLE || u.title || '').trim()
}));

const compareEmployeesByLastFirst = (left = {}, right = {}) => {
  const lastNameComparison = String(left?.lastName || '').trim().localeCompare(
    String(right?.lastName || '').trim(),
    undefined,
    { sensitivity: 'base' }
  );

  if (lastNameComparison !== 0) {
    return lastNameComparison;
  }

  return String(left?.firstName || '').trim().localeCompare(
    String(right?.firstName || '').trim(),
    undefined,
    { sensitivity: 'base' }
  );
};

const getEmployeeOptionLabel = (employee = {}) => {
  const firstName = String(employee?.firstName || '').trim();
  const lastName = String(employee?.lastName || '').trim();
  const fullName = lastName && firstName
    ? `${lastName}, ${firstName}`
    : (lastName || firstName);

  return fullName && employee.email ? `${fullName} (${employee.email})` : fullName || employee.email || 'Unknown employee';
};

const isFullyApprovedIncident = (incident = {}) => {
  const normalizedStatus = String(
    incident?.investigationStatus ||
    incident?.INVESTIGATION_STATUS ||
    incident?.status ||
    incident?.STATUS ||
    ''
  ).trim().toUpperCase();

  const supervisorApproved = String(incident?.SUPERVISOR_APPROVAL || incident?.supervisorApproval || '').trim().toUpperCase() === 'APPROVED';
  const managerApproved = String(incident?.MANAGER_APPROVAL || incident?.managerApproval || '').trim().toUpperCase() === 'APPROVED';
  const supervisorSignedOff = Number(incident?.SUPERVISOR_SIGN_OFF || incident?.supervisorSignOff || 0) === 1;
  const managerSignedOff = Number(incident?.MANAGER_SIGN_OFF || incident?.managerSignOff || 0) === 1;

  return normalizedStatus === 'COMPLETED'
    || normalizedStatus === 'RESOLVED'
    || (supervisorApproved && managerApproved)
    || (supervisorSignedOff && managerSignedOff);
};

const IncidentReportingDashboard = () => {
  // Error bubble and required field highlighting state
  const [showBubble, setShowBubble] = useState(false);
  const [fieldErrors, setFieldErrors] = useState([]);
  const [incidents, setIncidents] = useState([]);
  const [mainFormLoading, setMainFormLoading] = useState(false);
  const [processedIncidentIds, setProcessedIncidentIds] = useState([]);
  const isSubmittingForm = React.useRef(false);
  const submittedForms = React.useRef(new Set());

  const typesWithForms = [
    'First Aid',
    'Near Misses',
    'Work Refusal',
    'Vandalism',
    'Health & Safety Concerns / Issues',
    'Fatalities',
    'Hospital Admittance',
    'Injuries Lost-time',
    'Injuries Non-lost time',
    'OHS Serious',
    'OHS Potential Serious Incident'
  ];
  // Add Property/Equipment Damages to types with forms
  if (!typesWithForms.includes('Property/Equipment Damages')) typesWithForms.push('Property/Equipment Damages');
  // Add Vehicle Incident to types with forms
  if (!typesWithForms.includes('Vehicle Incident')) typesWithForms.push('Vehicle Incident');

  useEffect(() => {
    const fetchIncidents = async () => {
      try {
        console.log('📋 Component: Starting to fetch incidents...');
        const response = await getIncidents();
        console.log('📋 Component: Full getIncidents response:', response);
        
        // Check different possible response structures
        let incidentsArray = [];
        if (response && response.incidents && Array.isArray(response.incidents)) {
          incidentsArray = response.incidents;
          console.log('📋 Component: Using response.incidents, length:', incidentsArray.length);
        } else if (response && Array.isArray(response)) {
          incidentsArray = response;
          console.log('📋 Component: Using response directly as array, length:', incidentsArray.length);
        } else {
          console.log('📋 Component: Unexpected response structure:', response);
        }
        
        console.log('📋 Component: Setting incidents state with:', incidentsArray);
        setIncidents(incidentsArray);
        console.log('📋 Component: State should be updated now');
      } catch (error) {
        console.error('Error fetching incidents:', error);
      }
    };
    fetchIncidents();
  }, []);

  const incidentTypes = [
    'Fatalities',
    'First Aid',
    'Harassment and Violence',
    'Health & Safety Concerns / Issues',
    'Hospital Admittance',
    'Injuries Lost-time',
    'Injuries Non-lost time',
    'Near Misses',
    'OHS Potential Serious Incident',
    'OHS Serious',
    'Property/Equipment Damages',
    'Vandalism',
    'Vehicle Incident',
    'Work Refusal'
  ];

  // Separated departments into their own constant
  const departments = [
    'CAO',
    'Communications',
    'Finance',
    'Legislative Services',
    'Municipal Enforcement',
    'Parks',
    'Planning and Development',
    'Recreation',
    'Roads',
    'Safety',
    'Utilities',
  ];

  const [formVisible, setFormVisible] = useState(false);
  const [formData, setFormData] = useState({
    title: '', // Changed from [] to '' (empty string)
    location: '',
    severity: 'Medium',
    description: '',
    date: new Date().toISOString().split('T')[0], // Default to current date
    time: new Date().toTimeString().slice(0, 5), // Default to current time (HH:MM)
    completedByInjuredParty: 'No',
    primaryDepartment: '',
    employees: '',
    contractors: '',
    // New fields for WCB and OHS reporting
    wcbReportRequired: "false", // Default to No
    wcbClaimNumber: '', // Populated if WCB report is required
    wcbReportSubmittedDate: '', // Date WCB report was submitted
    ohsReportRequired: "false", // Default to No
    ohsReportNumber: '', // Populated if OHS report is required
    ohsReportSubmittedDate: '', // Date OHS report was submitted
    forwardToWCB: 'No', // Default to No
    lostDays: 0, // New field for lost days
  // Vehicle Incident specific defaults
  vehicleType: '',
  vehicleOther: '',
  thirdPartyInvolved: 'No',
  thirdPartyName: '',
  thirdPartyLicensePlate: '',
  thirdPartyInsurance: '',
  reportedToPoliceVehicle: 'No',
  policeReportDateVehicle: ''
  });

  const [showAdditionalForm, setShowAdditionalForm] = useState(false);
  const [currentIncidentType, setCurrentIncidentType] = useState('');
  const [currentIncidentId, setCurrentIncidentId] = useState(null); // New state value to hold the current ID
  const [additionalFormData, setAdditionalFormData] = useState({});
  const [pendingIncident, setPendingIncident] = useState(null);

  const [additionalFormLoading, setAdditionalFormLoading] = useState(false);
  const [additionalFormError, setAdditionalFormError] = useState(null);

  // Add witness state
  const [witnesses, setWitnesses] = useState([
    { name: '', phone: '', email: '', statement: '' }
  ]);

  // Add attachments state
  const [attachments, setAttachments] = useState([]);
  // Handles submission of additional forms and triggers alert
  const handleAdditionalFormSubmit = async (formData, incidentId) => {
    setAdditionalFormLoading(true);
    setAdditionalFormError(null);
    try {
      // Submit the additional form data to the backend
  console.log('handleAdditionalFormSubmit: submitting additional form', { incidentId, currentIncidentType, formData });
  const resp = await submitFormData(incidentId, currentIncidentType, formData);
  console.log('handleAdditionalFormSubmit: server response:', resp);
      // Mark this form as completed in state
      setAdditionalFormData(prev => ({
        ...prev,
        [currentIncidentType]: { ...formData, completed: true }
      }));
      setShowAdditionalForm(false);
      setCurrentIncidentType('');
      // Show acknowledgment dialog after additional form is submitted
      setShowAcknowledgment(true);
      alert('Incident report and additional form submitted successfully.');
    } catch (error) {
      setAdditionalFormError('Failed to submit additional form. Please try again.');
      console.error('Error submitting additional form:', error);
    } finally {
      setAdditionalFormLoading(false);
    }
  };

  // Add draft state management
  const [allDrafts, setAllDrafts] = useState([]);
  const [currentDraftId, setCurrentDraftId] = useState(null);
  const [showDrafts, setShowDrafts] = useState(false);

  // Add acknowledgment state
  const [showAcknowledgment, setShowAcknowledgment] = useState(false);
  const [acknowledgmentChecks, setAcknowledgmentChecks] = useState({
    accuracy: false,
    complete: false,
    authorized: false,
    understand: false
  });

  // Stats calculations
  const openIncidents = incidents.filter(i => i.status === 'Open').length;
  const resolvedIncidents = incidents.filter(isFullyApprovedIncident).length;
  const highSeverity = incidents.filter(i => i.severity === 'High').length;
  
  // Debug the stats calculations
  console.log('📊 STATS DEBUG:');
  console.log('📊 Total incidents array:', incidents);
  console.log('📊 Total incidents length:', incidents.length);
  console.log('📊 Open incidents count:', openIncidents);
  console.log('📊 Resolved incidents count:', resolvedIncidents);
  console.log('📊 High severity count:', highSeverity);
  if (incidents.length > 0) {
    console.log('📊 Sample incident structure:', incidents[0]);
    console.log('📊 All incident statuses:', incidents.map(i => i.status));
  }

  const [employees, setEmployees] = useState([]);
  const [allEmployees, setAllEmployees] = useState([]);
  const currentUserEmail = getCurrentDraftUserEmail();
  const drafts = allDrafts.filter(draft => canUserAccessIncidentDraft(draft, currentUserEmail));

  // Fetch and normalize employees. Re-run when primaryDepartment changes so
  // the injured-party dropdown shows the correct employees for the selected dept.
  useEffect(() => {
    const fetchEmployees = async () => {
      try {
        const dept = formData.primaryDepartment;
        console.log('Fetching employees, department=', dept || '(all)');

        const url = dept ? `/api/users?department=${encodeURIComponent(dept)}` : '/api/users';
        const resp = await api.get(url);
        console.log('Fetch employees raw response:', resp && resp.data);

        let raw = [];
        if (!resp || !resp.data) raw = [];
        else if (Array.isArray(resp.data)) raw = resp.data;
        else if (Array.isArray(resp.data.users)) raw = resp.data.users;
        else if (Array.isArray(resp.data.usersList)) raw = resp.data.usersList;
        else raw = [];

        const normalized = normalizeEmployeeList(raw);

        console.log('Normalized employees:', normalized);
        setEmployees(normalized);
      } catch (error) {
        console.error('Error fetching employees:', error);
        setEmployees([]);
      }
    };
    fetchEmployees();
  }, [formData.primaryDepartment]);

  useEffect(() => {
    const fetchAllEmployees = async () => {
      try {
        const resp = await api.get('/api/users', { params: { scope: 'all' } });

        let raw = [];
        if (!resp || !resp.data) raw = [];
        else if (Array.isArray(resp.data)) raw = resp.data;
        else if (Array.isArray(resp.data.users)) raw = resp.data.users;
        else if (Array.isArray(resp.data.usersList)) raw = resp.data.usersList;

        const normalized = normalizeEmployeeList(raw)
          .filter((employee) => employee.email)
          .sort(compareEmployeesByLastFirst);

        setAllEmployees(normalized);
      } catch (error) {
        console.error('Error fetching full employee list:', error);
        setAllEmployees([]);
      }
    };

    fetchAllEmployees();
  }, []);

  // Load drafts from localStorage on component mount
  useEffect(() => {
    const savedDrafts = localStorage.getItem(INCIDENT_DRAFTS_STORAGE_KEY);
    if (savedDrafts) {
      try {
        const parsedDrafts = JSON.parse(savedDrafts);
        const hydratedDrafts = Array.isArray(parsedDrafts)
          ? parsedDrafts.map(hydrateIncidentDraft)
          : [];

        setAllDrafts(hydratedDrafts);
        localStorage.setItem(INCIDENT_DRAFTS_STORAGE_KEY, JSON.stringify(hydratedDrafts));
      } catch (error) {
        console.error('Error loading drafts:', error);
        localStorage.removeItem(INCIDENT_DRAFTS_STORAGE_KEY);
      }
    }
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
  if (name === 'title') console.log('Incident type changed to:', value);
    setFormData({
      ...formData,
      [name]: value
    });
    if (name === 'title') {
      // keep currentIncidentType in sync so additional form flows render correctly
      setCurrentIncidentType(value);
    }
  };

  const resolveSelectedSupervisorAssignee = (source = formData) => {
    const selectedSupervisor = String(source?.supervisor || '').trim();
    return selectedSupervisor || 'Unassigned';
  };

  const handleSubmit = async (e) => {
    console.log("🚀 MAIN FORM handleSubmit called");
    console.log("🚀 Event target:", e.target);
    console.log("🚀 Current formData:", formData);
    e.preventDefault();
    if (mainFormLoading) return;

    // Defensive: ensure we are not reusing an old incident
    if (pendingIncident) {
      alert("Previous report was not finalized. Please finalize or reset before submitting a new report.");
      return;
    }
    setMainFormLoading(true);
    console.log("pendingIncident at submit:", pendingIncident);

    try {
      const requiresWCB = formData.title === 'Injuries Lost-time' ||
                          formData.title === 'Injuries Non-lost time' ||
                          formData.title === 'Hospital Admittance' ||
                          formData.title === 'Fatalities';

      const requiresOHS = formData.title === 'OHS Serious' ||
                          formData.title === 'OHS Potential Serious Incident' ||
                          formData.title === 'Fatalities';

      const newIncident = {
        ...formData,
        status: 'Open',
        assignee: resolveSelectedSupervisorAssignee(formData),
        wcbReportRequired: formData.wcbReportRequired === "true" || requiresWCB,
        ohsReportRequired: formData.ohsReportRequired === "true" || requiresOHS,
        witnesses: witnesses.filter(w => w.name.trim() !== ""),
        attachments: attachments,
      };

      console.log('🔍 DEBUG: Incident title:', formData.title);
      console.log('🔍 DEBUG: Creating main incident with all data');

      // Create the main incident
      const response = await createIncident(newIncident);
      const incidentId = extractIncidentId(response);
      console.log('✅ Main incident created with ID:', incidentId);

      // Save the created incident ID into component state so additional
      // forms and later steps use the correct ID when posting detail rows.
      if (incidentId) {
        setCurrentIncidentId(incidentId);
      }

      if (!incidentId) {
        console.error('❌ No incident ID found in response:', response);
        alert("Failed to create incident: No incident ID returned.");
        setMainFormLoading(false);
        return;
      }

      // Upload attachments to server now that we have an incident ID
      if (attachments.length > 0) {
        console.log(`📎 Uploading ${attachments.length} attachments for incident ${incidentId}`);
        try {
          await uploadAttachmentsToServer(incidentId);
          console.log('✅ All attachments uploaded successfully');
        } catch (uploadError) {
          console.error('❌ Failed to upload some attachments:', uploadError);
          // Continue with form submission even if attachments fail
          alert('Warning: Some attachments may not have been uploaded properly.');
        }
      }

      // Now submit type-specific data if needed
      const needsAdditionalForms = typesWithForms.includes(formData.title);
      if (needsAdditionalForms) {
        console.log('� Submitting type-specific data for:', formData.title);
        
        // Extract type-specific form data
        let typeSpecificData = {};
        
        switch (formData.title) {
          case 'First Aid':
            typeSpecificData = {
              treatmentProvided: formData.treatmentProvided,
              bodyPartAffected: formData.bodyPartAffected,
              injuryType: formData.injuryType,
              medicalAttentionRequired: formData.medicalAttentionRequired
            };
            break;
          case 'Near Misses':
            typeSpecificData = {
              nearMissDate: formData.nearMissDate,
              nearMissTime: formData.nearMissTime,
              location: formData.location,
              description: formData.description,
              reportedBy: formData.employees || 'Unknown',
              actionsTaken: formData.actionsTaken,
              supervisor: formData.supervisor
            };
            break;
          case 'Work Refusal':
            typeSpecificData = {
              issueDescription: formData.issueDescription,
              investigationCause: formData.investigationCause,
              supervisorNotified: formData.supervisorNotified,
              supervisorName: formData.supervisorName,
              resolutionSteps: formData.resolutionSteps,
              hazardMitigated: formData.hazardMitigated,
              photoUrls: []  // Empty for now, can be enhanced later
            };
            break;
          case 'Vandalism':
            typeSpecificData = {
              damageType: formData.damageType,
              estimatedCost: formData.estimatedCost,
              itemsDamaged: formData.itemsDamaged,
              suspectedPerpetrator: formData.suspectedPerpetrator,
              reportedToPolice: formData.reportedToPolice,
              policeReportNumber: formData.policeReportNumber
            };
            break;
          default:
            // For incident types that don't have specific forms yet
            typeSpecificData = {};
            break;
        }

        // Submit the type-specific data only if we have fields for this type
        if (Object.keys(typeSpecificData).length > 0) {
          console.log('� Submitting type-specific data:', typeSpecificData);
          await submitFormData(incidentId, formData.title, typeSpecificData);
          console.log('✅ Type-specific data submitted successfully');
        }
      }

      if (needsAdditionalForms) {
        // Show acknowledgment dialog for all incident types
        setPendingIncident({ ...newIncident, id: incidentId, incidentId, ID: incidentId });
        setMainFormLoading(false);
        setShowAcknowledgment(true);
      } else {
        // No additional forms needed, submission is complete
        setFormVisible(false);
        setCurrentIncidentId(null);
        setAdditionalFormData({});
        setFormData({
          title: '',
          location: '',
          severity: 'Medium',
          description: '',
          date: new Date().toISOString().split('T')[0],
          time: new Date().toTimeString().slice(0, 5),
          completedByInjuredParty: 'No',
          primaryDepartment: '',
          employees: '',
          contractors: '',
          wcbReportRequired: "false",
          wcbClaimNumber: '',
          wcbReportSubmittedDate: '',
          ohsReportRequired: "false",
          ohsReportNumber: '',
          ohsReportSubmittedDate: '',
          forwardToWCB: 'No',
          lostDays: 0,
        });
        setWitnesses([{ name: '', phone: '', email: '', statement: '' }]);
        setAttachments([]);
        setMainFormLoading(false);
        alert("Incident submitted successfully.");
      }

    } catch (error) {
      console.error('❌ Error in form submission:', error);
      alert('An error occurred during submission. Please try again.');
      setMainFormLoading(false);
    }
  };

  const handleAcknowledgmentSubmit = async () => {
    // Check if all acknowledgments are checked
    const allChecked = Object.values(acknowledgmentChecks).every(checked => checked);
    if (!allChecked) {
      alert('Please acknowledge all statements before submitting.');
      return;
    }

    setShowAcknowledgment(false);
    setMainFormLoading(true);

    let incidentToSubmit;
    if (pendingIncident) {
      incidentToSubmit = pendingIncident;
    } else {
      incidentToSubmit = {
        ...formData,
        status: 'Open',
        assignee: resolveSelectedSupervisorAssignee(formData),
        wcbReportRequired: formData.wcbReportRequired === "true" || (formData.title === 'Injuries Lost-time' || formData.title === 'Injuries Non-lost time' || formData.title === 'Hospital Admittance' || formData.title === 'Fatalities'),
        ohsReportRequired: formData.ohsReportRequired === "true" || (formData.title === 'OHS Serious' || formData.title === 'OHS Potential Serious Incident' || formData.title === 'Fatalities'),
        witnesses: witnesses.filter(w => w.name.trim() !== ""),
        attachments: attachments,
      };
    }
    try {
      console.log("🟡 handleAcknowledgmentSubmit: calling finalizeSubmission with incident", incidentToSubmit);
      await finalizeSubmission(incidentToSubmit);
    } catch (error) {
      console.error('❌ Error in acknowledgment submission:', error);
      alert('An error occurred during submission. Please try again.');
    } finally {
      setMainFormLoading(false);
    }
  };

  const showNextAdditionalForm = (incidentWithId = pendingIncident, updatedFormData = additionalFormData) => {
    // Use the incident's title as a string directly
    const incidentType = incidentWithId && incidentWithId.title ? incidentWithId.title : '';
    
    // Check if this incident type needs a form and hasn't been completed
    if (typesWithForms.includes(incidentType) && 
        (!updatedFormData[incidentType] || !updatedFormData[incidentType].completed)) {
      setCurrentIncidentType(incidentType);
      setShowAdditionalForm(true);
    } else {
      // No more forms needed, show acknowledgment dialog
      setShowAcknowledgment(true);
    }
  };

  // PATCH: Ensure finalizeSubmission always calls sendWCBAndOHSNotifications
  const finalizeSubmission = async (incident) => {
  console.log("🟢 finalizeSubmission called with incident:", incident);
    try {
      // Always use currentIncidentId as the source of truth
      let incidentId = currentIncidentId || incident.id;

      console.log('📝 Finalizing submission for incident ID:', incidentId);

      if (!incidentId) {
        const mainIncident = {
          ...incident,
          title: incident.title
        };
        const response = await createIncident(mainIncident);
        incidentId = extractIncidentId(response);
        if (!incidentId) {
          alert("Failed to create incident: No incident ID returned.");
          return;
        }
        // Persist the created id to component state so subsequent calls use it
        try {
          setCurrentIncidentId(incidentId);
        } catch (e) {
          // ignore if state setter not ready in some contexts
        }
      }

      const pendingUploads = (Array.isArray(attachments) ? attachments : []).filter(
        (attachment) => attachment?.file && !attachment?.uploaded
      );

      if (pendingUploads.length > 0) {
        console.log(`📎 Finalize step uploading ${pendingUploads.length} pending attachment(s) for incident ${incidentId}`);
        await uploadAttachmentsToServer(incidentId);
      }

      // If additional form data wasn't submitted separately but the main
      // incident contains type-specific fields (user filled inline), submit
      // those now so *_DETAILS rows are created for all supported types.
      try {
        const incidentType = (incident && incident.title) || '';
        if (typesWithForms.includes(incidentType)) {
          const alreadySubmitted = additionalFormData && additionalFormData[incidentType] && additionalFormData[incidentType].completed;
          if (!alreadySubmitted) {
            // Build type-specific payload from the main incident object
            let typeSpecificData = {};
            switch (incidentType) {
              case 'Near Misses':
                typeSpecificData = {
                  nearMissDate: incident.nearMissDate,
                  nearMissTime: incident.nearMissTime,
                  location: incident.location,
                  description: incident.description,
                  reportedBy: incident.employees || incident.reportedBy || 'Unknown',
                  actionsTaken: incident.actionsTaken,
                  supervisor: incident.supervisor
                };
                break;
              case 'First Aid':
                typeSpecificData = {
                  treatmentProvided: incident.treatmentProvided,
                  bodyPartAffected: incident.bodyPartAffected,
                  injuryType: incident.injuryType,
                  medicalAttentionRequired: incident.medicalAttentionRequired
                };
                break;
              case 'Work Refusal':
                typeSpecificData = {
                  issueDescription: incident.issueDescription,
                  investigationCause: incident.investigationCause,
                  supervisorNotified: incident.supervisorNotified,
                  supervisorName: incident.supervisorName,
                  resolutionSteps: incident.resolutionSteps,
                  hazardMitigated: incident.hazardMitigated,
                  photoUrls: incident.photoUrls || incident.photos || []
                };
                break;
              case 'Vandalism':
                typeSpecificData = {
                  damageType: incident.damageType,
                  estimatedCost: incident.estimatedCost,
                  itemsDamaged: incident.itemsDamaged,
                  suspectedPerpetrator: incident.suspectedPerpetrator,
                  reportedToPolice: incident.reportedToPolice,
                  policeReportNumber: incident.policeReportNumber
                };
                break;
              case 'Health & Safety Concerns / Issues':
                typeSpecificData = {
                  issueDescription: incident.issueDescription,
                  isRecurring: incident.isRecurring || false,
                  previousIssueDetails: incident.previousIssueDetails || null,
                  additionalNotifications: incident.additionalNotifications || null,
                  selectedManager: incident.selectedManager || null,
                  selectedCommitteeMember: incident.selectedCommitteeMember || null,
                  photos: null,
                  isAnonymous: incident.isAnonymous || false
                };
                break;
              case 'Injuries Lost-time':
                typeSpecificData = {
                  employeeName: incident.employeeName || incident.employees || null,
                  dateOfInjury: incident.dateOfInjury || incident.date || null,
                  hoursLost: incident.hoursLost || incident.lostDays || 0,
                  description: incident.description || null
                };
                break;
              case 'Injuries Non-lost time':
                typeSpecificData = {
                  employeeName: incident.employeeName || incident.employees || null,
                  dateOfInjury: incident.dateOfInjury || incident.date || null,
                  description: incident.description || null
                };
                break;
              case 'Hospital Admittance':
                typeSpecificData = {
                  patientName: incident.patientName || incident.employeeName || null,
                  dateOfBirth: incident.dateOfBirth || null,
                  admissionDate: incident.admissionDate || incident.date || null,
                  reason: incident.reason || incident.description || null,
                  doctor: incident.doctor || null,
                  notes: incident.notes || null
                };
                break;
              case 'Fatalities':
                typeSpecificData = {
                  name: incident.name || incident.employeeName || null,
                  cause: incident.cause || null,
                  date: incident.date || null
                };
                break;
              case 'OHS Serious':
                typeSpecificData = {
                  details: incident.details || incident.fileNumber || null
                };
                break;
              case 'OHS Potential Serious Incident':
                typeSpecificData = {
                  details: incident.details || null
                };
                break;
              default:
                typeSpecificData = {};
            }

            if (typeSpecificData && Object.keys(typeSpecificData).length > 0) {
              console.log('Finalizing submission: submitting derived type-specific data:', { incidentId, incidentType, typeSpecificData });
              await submitFormData(incidentId, incidentType, typeSpecificData);
              // Mark as completed locally
              setAdditionalFormData(prev => ({ ...prev, [incidentType]: { ...typeSpecificData, completed: true } }));
            }
          }
        }
      } catch (err) {
        console.error('Error submitting derived type-specific data during finalizeSubmission:', err);
      }

      for (const [formType, formData] of Object.entries(additionalFormData)) {
        if (formData?.completed) {
          continue;
        }
        const { completed, ...cleanFormData } = formData;
        console.log('Submitting formType:', formType, 'formData:', cleanFormData);
        await submitFormData(incidentId, formType, cleanFormData);
      }

      // Fetch the updated incident for notification context
      const updatedIncidentsResponse = await getIncidents();
      const updatedIncidents = updatedIncidentsResponse.incidents || updatedIncidentsResponse || [];
      const submittedIncident = updatedIncidents.find(i => i.id === incidentId || i.ID === incidentId);

      // --- PATCH: Call notification forwarding here ---
      if (submittedIncident) {
        await sendWCBAndOHSNotifications(submittedIncident);
      } else {
        // fallback if not found
        await sendWCBAndOHSNotifications({ ...incident, id: incidentId });
      }

      setFormVisible(false);
      setCurrentIncidentId(null);
      setAdditionalFormData({});
      setFormData(getDefaultFormData());
      setWitnesses([{ name: '', phone: '', email: '', statement: '' }]);
      setAttachments([]);
      setMainFormLoading(false);
      setShowAdditionalForm(false);
      setCurrentIncidentType('');
      
      // Remove draft after successful submission
      if (currentDraftId) {
        deleteDraft(currentDraftId);
        setCurrentDraftId(null);
      }
      
      setTimeout(() => {
        getIncidents();
      }, 500);
      console.log("✅ Showing success alert for incident submission");
      alert("Incident submitted successfully.");
      setTimeout(() => {
        window.location.replace(getAppPath('/incident-reports'));
      }, 500);
    } catch (error) {
      setMainFormLoading(false);
      console.error("❌ Failed to submit incident: ", error);
      alert("Failed to submit incident. Please try again. [DEBUG]");
    }
  };

  // Add this to the reset function
  const resetForm = () => {
    // Clear the submission tracking when resetting the form
    submittedForms.current.clear();
    console.log("Resetting form and clearing pendingIncident");
    setFormData({
      title: '',
      location: '',
      severity: 'Medium',
      description: '',
      date: new Date().toISOString().split('T')[0],
      time: new Date().toTimeString().slice(0, 5),
      completedByInjuredParty: 'No',
      primaryDepartment: '',
      employees: '',
      contractors: '',
      wcbReportRequired: "false",
      wcbClaimNumber: '',
      wcbReportSubmittedDate: '',
      ohsReportRequired: "false",
      ohsReportNumber: '',
      ohsReportSubmittedDate: '',
      forwardToWCB: 'No',
      lostDays: 0,
    });
    setWitnesses([{ name: '', phone: '', email: '', statement: '' }]);
    setAttachments([]);
    setAdditionalFormData({});
    setPendingIncident(null); // <-- This is critical
    setCurrentDraftId(null); // Clear draft ID
    setFormVisible(false);
    // Reset acknowledgment state
    resetAcknowledgment();
  };

  const getSeverityColor = (severity) => {
    switch (severity) {
      case 'High': return 'severity-high';
      case 'Medium': return 'severity-medium';
      case 'Low': return 'severity-low';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'Open': return 'status-open';
      case 'In Progress': return 'status-in-progress';
      case 'Resolved': return 'status-resolved';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const renderAdditionalForm = (props = {}) => {
  const sharedProps = {
      onFormSubmit: handleAdditionalFormSubmit,
      loading: additionalFormLoading,
      error: additionalFormError,
      incidentId: currentIncidentId // <-- Pass the latest incidentId
    };
    // let child forms report changes back to parent so parent can submit when ready
    sharedProps.onFormChange = (form) => {
      const key = props.incidentType || currentIncidentType || formData.title || 'Property/Equipment Damages';
      setAdditionalFormData(prev => ({ ...prev, [key]: form }));
    };
  // Determine which incident type we should render for:
  const incidentType = props.incidentType || currentIncidentType || formData.title || '';
  console.log('renderAdditionalForm: incidentType=', incidentType, 'currentIncidentType=', currentIncidentType);
  switch(incidentType) {
      case 'First Aid':
        return <FirstAidForm {...sharedProps} {...props} />;
      case 'Vandalism':
        return <VandalismForm {...sharedProps} {...props} />;
      case 'Health & Safety Concerns / Issues':
        return (
          <SafetyConcernsForm
            onFormSubmit={handleAdditionalFormSubmit}
            loading={additionalFormLoading} // Pass the loading state
            error={additionalFormError}
            incidentId={currentIncidentId}
          />
        );
      case 'Near Misses':
        return <NearMissForm {...sharedProps} {...props} />;
      case 'Work Refusal':
        return <WorkRefusalForm {...sharedProps} {...props} />;
      case 'Fatalities':
        return <FatalitiesForm {...sharedProps} {...props} />;
      case 'Hospital Admittance':
        return <HospitalAdmittanceForm {...sharedProps} {...props} />;
      case 'Injuries Lost-time':
        return <InjuriesLostTimeForm {...sharedProps} {...props} />;
      case 'Injuries Non-lost time':
        return <InjuriesNonLostTimeForm {...sharedProps} {...props} />;
      case 'OHS Serious':
        return <OHSSeriousForm {...sharedProps} {...props} />;
      case 'OHS Potential Serious Incident':
        return <PSIForm {...sharedProps} {...props} />;
      case 'Property/Equipment Damages':
        return <PropertyEquipmentDamagesForm {...sharedProps} {...props} initialData={additionalFormData && additionalFormData['Property/Equipment Damages'] ? additionalFormData['Property/Equipment Damages'] : {}} onSaved={() => { setAdditionalFormLoading(false); }} />;
      case 'Vehicle Incident':
        return <VehicleIncidentForm {...sharedProps} {...props} initialData={additionalFormData && additionalFormData['Vehicle Incident'] ? additionalFormData['Vehicle Incident'] : {}} />;
      default:
        return null;
    }
  };

  const addWitness = () => {
    setWitnesses([...witnesses, { name: '', phone: '', email: '', statement: '' }]);
  };

  const removeWitness = (index) => {
    const newWitnesses = [...witnesses];
    newWitnesses.splice(index, 1);
    setWitnesses(newWitnesses);
  };

  const handleWitnessChange = (index, field, value) => {
    const newWitnesses = [...witnesses];
    newWitnesses[index][field] = value;
    setWitnesses(newWitnesses);
  };

  const handleFileUpload = (event) => {
    const files = Array.from(event.target.files);
    const newAttachments = files.map(file => ({
      id: Date.now() + Math.random(),
      file: file,
      name: file.name,
      size: file.size,
      type: file.type,
      uploaded: false // Track if file has been uploaded to server
    }));
    setAttachments(prev => [...prev, ...newAttachments]);
  };

  // New function to upload attachments to server after incident is created
  const uploadAttachmentsToServer = async (incidentId) => {
    const canonicalIncidentId = incidentId ? String(incidentId).trim() : '';
    if (!canonicalIncidentId) {
      throw new Error('Missing canonical incident ID for attachment upload');
    }

    const uploadPromises = attachments.map(async (attachment) => {
      if (attachment.uploaded) return; // Skip already uploaded files
      
      const formData = new FormData();
      formData.append('file', attachment.file);
      formData.append('incidentId', canonicalIncidentId);
      
      try {
        console.log(`📎 Uploading attachment from #file-upload: ${attachment.name} for incident ${canonicalIncidentId}`);
        // IMPORTANT: do NOT set the Content-Type header manually for multipart/form-data;
        // letting the browser/axios set it ensures the correct boundary is included and
        // form fields (like incidentId) are parsed by the server.
        const response = await api.post(`/api/incidents/attachments?incidentId=${encodeURIComponent(canonicalIncidentId)}`, formData);

        console.log('Upload response for', attachment.name, response && response.data ? response.data : response);

        if (response && response.data && response.data.success) {
          const uploadedAttachmentId = response.data.fileId || (response.data.attachment && response.data.attachment.id);

          if (uploadedAttachmentId) {
            try {
              await api.post(`/api/incidents/${canonicalIncidentId}/attachment-links`, {
                attachmentId: uploadedAttachmentId
              });
              console.log(`🔗 Ensured attachment ${uploadedAttachmentId} is linked to incident ${canonicalIncidentId}`);
            } catch (linkError) {
              console.error(`❌ Failed to ensure link for attachment ${uploadedAttachmentId} -> incident ${canonicalIncidentId}:`, linkError && linkError.response ? linkError.response.data || linkError.response : linkError);
              throw linkError;
            }
          }

          console.log(`✅ Successfully uploaded ${attachment.name}`);
          attachment.uploaded = true;
          attachment.serverId = uploadedAttachmentId;
        }
      } catch (error) {
        console.error(`❌ Failed to upload ${attachment.name}:`, error && error.response ? error.response.data || error.response : error);
        throw error;
      }
    });
    
    await Promise.all(uploadPromises);
    console.log(`✅ All ${attachments.length} attachments uploaded successfully`);
  };

  const removeAttachment = (id) => {
    setAttachments(prev => prev.filter(attachment => attachment.id !== id));
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  // Draft management functions
  const saveDraft = () => {
    const existingDraft = allDrafts.find(draft => draft.id === currentDraftId);
    const subjectEmail = existingDraft?.createdForEmail || getDraftSubjectEmail(formData) || currentUserEmail;
    const creatorEmail = existingDraft?.createdByEmail || currentUserEmail;
    const visibleToEmails = Array.from(
      new Set(
        [
          ...(Array.isArray(existingDraft?.visibleToEmails) ? existingDraft.visibleToEmails : []),
          creatorEmail,
          subjectEmail,
          currentUserEmail
        ]
          .map(normalizeDraftEmail)
          .filter(Boolean)
      )
    );

    const draftData = hydrateIncidentDraft({
      id: currentDraftId || Date.now(),
      formData: formData,
      witnesses: witnesses,
      attachments: attachments.map(att => ({
        id: att.id,
        name: att.name,
        size: att.size,
        type: att.type
        // Note: We can't save the actual file object, just metadata
      })),
      savedAt: new Date().toISOString(),
      title: formData.title || 'Untitled Draft',
      createdByEmail: creatorEmail,
      createdForEmail: subjectEmail,
      visibleToEmails
    });

    const updatedDrafts = allDrafts.filter(d => d.id !== draftData.id);
    updatedDrafts.push(draftData);
    
    setAllDrafts(updatedDrafts);
    setCurrentDraftId(draftData.id);
    localStorage.setItem(INCIDENT_DRAFTS_STORAGE_KEY, JSON.stringify(updatedDrafts));
    
    alert('Draft saved successfully. Attachments are not saved with drafts and must be re-uploaded before submission.');
  };

  const loadDraft = (draft) => {
    setFormData(draft.formData);
    setWitnesses(draft.witnesses || [{ name: '', phone: '', email: '', statement: '' }]);
    // Note: Attachments need to be re-uploaded as we can't restore file objects
    setAttachments([]);
    setCurrentDraftId(draft.id);
    setFormVisible(true);
    setShowDrafts(false);
    
    if (draft.attachments && draft.attachments.length > 0) {
      alert('Note: Attachments will need to be re-uploaded as they cannot be restored from draft.');
    }
  };

  const deleteDraft = (draftId) => {
    const updatedDrafts = allDrafts.filter(d => d.id !== draftId);
    setAllDrafts(updatedDrafts);
    localStorage.setItem(INCIDENT_DRAFTS_STORAGE_KEY, JSON.stringify(updatedDrafts));
    
    if (currentDraftId === draftId) {
      setCurrentDraftId(null);
    }
  };

  const handleAcknowledgmentChange = (field) => {
    setAcknowledgmentChecks(prev => ({
      ...prev,
      [field]: !prev[field]
    }));
  };

  const resetAcknowledgment = () => {
    setAcknowledgmentChecks({
      accuracy: false,
      complete: false,
      authorized: false,
      understand: false
    });
    setShowAcknowledgment(false);
  };

  const startNewIncident = () => {
    setFormData(getDefaultFormData());
    setWitnesses([{ name: '', phone: '', email: '', statement: '' }]);
    setAttachments([]);
    setCurrentDraftId(null);
    setFormVisible(true);
    setShowDrafts(false);
  };

  return (
    <div className="bg-gray-50 min-h-screen">
      <div className="container mt-4">
        <div className="flex justify-between items-center mb-4">
          <div className="flex gap-2">
            <button 
              onClick={() => setFormVisible(!formVisible)}
              className="button"
            >
              {formVisible ? 'Cancel Report' : 'Report New Incident'}
            </button>
            {!formVisible && (
              <button 
                onClick={() => setShowDrafts(!showDrafts)}
                className="button bg-gray-600 hover:bg-gray-700"
              >
                {showDrafts ? 'Hide Drafts' : `View Drafts (${drafts.length})`}
              </button>
            )}
          </div>
        </div>

        <main>
          {showDrafts && !formVisible ? (
            <div className="form-container">
              <h2 className="text-xl font-semibold mb-4">Saved Drafts</h2>
              {drafts.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <p>No saved drafts found.</p>
                  <button 
                    onClick={startNewIncident}
                    className="mt-4 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700"
                  >
                    Start New Incident Report
                  </button>
                </div>
              ) : (
                <div className="space-y-4">
                  {drafts.map((draft) => (
                    <div key={draft.id} className="border border-gray-200 rounded-lg p-4 bg-white">
                      <div className="flex justify-between items-start">
                        <div className="flex-1">
                          <h3 className="font-medium text-lg">{draft.title}</h3>
                          <p className="text-sm text-gray-600 mt-1">
                            Saved: {new Date(draft.savedAt).toLocaleString()}
                          </p>
                          <div className="mt-2 text-sm text-gray-700">
                            <span className="inline-block mr-4">
                              <strong>Location:</strong> {draft.formData.location || 'Not specified'}
                            </span>
                            <span className="inline-block mr-4">
                              <strong>Severity:</strong> {draft.formData.severity}
                            </span>
                            <span className="inline-block">
                              <strong>Department:</strong> {draft.formData.primaryDepartment ? getDepartmentDisplayName(draft.formData.primaryDepartment) : 'Not specified'}
                            </span>
                          </div>
                          {draft.attachments && draft.attachments.length > 0 && (
                            <p className="text-sm text-gray-600 mt-1">
                              📎 {draft.attachments.length} attachment(s) (will need to be re-uploaded)
                            </p>
                          )}
                        </div>
                        <div className="flex gap-2 ml-4">
                          <button
                            onClick={() => loadDraft(draft)}
                            className="bg-blue-600 text-white px-3 py-1 rounded text-sm hover:bg-blue-700"
                          >
                            Continue
                          </button>
                          <button
                            onClick={() => {
                              if (window.confirm('Are you sure you want to delete this draft?')) {
                                deleteDraft(draft.id);
                              }
                            }}
                            className="bg-red-600 text-white px-3 py-1 rounded text-sm hover:bg-red-700"
                          >
                            Delete
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                  <div className="text-center mt-6">
                    <button 
                      onClick={startNewIncident}
                      className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700"
                    >
                      Start New Incident Report
                    </button>
                  </div>
                </div>
              )}
            </div>
          ) : formVisible ? (
            <div className="form-container">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-xl font-semibold">
                  {currentDraftId ? 'Edit Draft - ' : ''}Report a New Incident
                </h2>
                {currentDraftId && (
                  <span className="bg-yellow-100 text-yellow-800 px-2 py-1 rounded-md text-sm">
                    Editing Draft
                  </span>
                )}
              </div>
              <form onSubmit={handleSubmit}>
                {showBubble && (
                  <div style={{ background: '#ffeaea', color: '#b71c1c', border: '1px solid #b71c1c', borderRadius: 6, padding: '10px', marginBottom: 16 }}>
                    <strong>Please fill in all required fields.</strong>
                  </div>
                )}

                <div className="form-group">
                  <label className="label">Are you completing this on behalf of the injured party?</label>
                  <div className="radio-group">
                    <label className="radio-label">
                      <input
                        type="radio"
                        name="completedByInjuredParty"
                        value="Yes"
                        checked={formData.completedByInjuredParty === 'Yes'}
                        onChange={handleInputChange}
                      />
                      Yes
                    </label>
                    <label className="radio-label">
                      <input
                        type="radio"
                        name="completedByInjuredParty"
                        value="No"
                        checked={formData.completedByInjuredParty === 'No'}
                        onChange={handleInputChange}
                      />
                      No
                    </label>
                    {formData.completedByInjuredParty === 'Yes' && (
                      <div className="form-group">
                        <label className="label" htmlFor="employees">Injured Worker's Name</label>
                        <select
                          className={`select${fieldErrors.includes('primaryDepartment') ? ' border-red-500' : ''}`}
                          id="employees"
                          name="employees"
                          value={formData.employees}
                          onChange={handleInputChange}
                        >
                          <option value="">Select an employee</option>
                          {employees
                            .filter(emp => String(emp.jobTitle || emp.title || '').trim().toLowerCase() !== 'volunteer firefighter')
                            .slice()
                            .sort(compareEmployeesByLastFirst)
                            .map((emp) => (
                            <option key={emp.id || emp.email} value={emp.email}>
                              {getEmployeeOptionLabel(emp)}
                            </option>
                          ))}
                        </select>

                      </div>
                    )}
                  </div>
                </div>

                <div className="form-group">
                  <label className="label" htmlFor="primaryDepartment">
                    Primary Department
                  </label>
                  <select
                    className="select"
                    id="primaryDepartment"
                    name="primaryDepartment"
                    value={formData.primaryDepartment}
                    onChange={handleInputChange}
                    required
                  >
                    <option value="">Select a department</option>
                    {departments.map((department) => (
                      <option key={department} value={department}>
                        {getDepartmentDisplayName(department)}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="mb-6 border border-gray-200 rounded-md p-4">
                  <div className="mb-4">
                    <h3 className="text-lg font-medium">Incident Details</h3>
                  </div>
                  <div>
                    <div className="mb-4">
                      <label className="block text-gray-700 mb-1">Location</label>
                      <input
                        type="text"
                        name="location"
                        value={formData.location}
                        onChange={handleInputChange}
                        className={`w-full border rounded-md px-3 py-2${fieldErrors.includes('location') ? ' border-red-500' : ''}`}
                        required
                      />
                    </div>

                    <div className="mb-4">
                      <label className="block text-gray-700 mb-1" htmlFor="incident-types">
                        Incident Type
                      </label>
                      <select
                        id="incident-types"
                        name="title"
                        value={formData.title || ''}
                        onChange={handleInputChange}
                        className={`w-full border rounded-md px-3 py-2${fieldErrors.includes('title') ? ' border-red-500' : ''}`}
                        required
                      >
                        <option value="">Select an incident type</option>
                        {incidentTypes.map((type) => (
                          <option key={type} value={type}>
                            {type}
                          </option>
                        ))}
                      </select>
                      {/* Render SafetyConcernsForm directly under Incident Type dropdown if selected */}
                      {formData.title === 'Health & Safety Concerns / Issues' && (
                        <div className="mt-4">
                          <SafetyConcernsForm
                            formData={additionalFormData['Health & Safety Concerns / Issues'] || { isAnonymous: !!formData.submitAnonymously }}
                            onFormSubmit={handleAdditionalFormSubmit}
                            onFormChange={(form) => {
                              setAdditionalFormData(prev => ({
                                ...prev,
                                'Health & Safety Concerns / Issues': form
                              }));
                              setFormData(prev => ({
                                ...prev,
                                issueDescription: form.issueDescription || '',
                                isRecurring: form.isRecurring || 'No',
                                previousIssueDetails: form.previousIssueDetails || '',
                                additionalNotifications: form.additionalNotifications || 'No',
                                selectedManager: form.selectedManager || null,
                                selectedCommitteeMember: form.selectedCommitteeMember || null,
                                photos: null,
                                isAnonymous: !!form.isAnonymous,
                                submitAnonymously: !!form.isAnonymous
                              }));
                            }}
                            loading={additionalFormLoading}
                            error={additionalFormError}
                            incidentId={currentIncidentId}
                            showSubmitButton={false}
                          />
                        </div>
                      )}
                    </div>

                    {/* Removed Forward to WBC/Payroll Clerk field */}

                    {/* Removed lost days conditional on Forward to WBC/Payroll Clerk */}
                    
                  </div>
                </div>


                <div className="mb-4">
                  <label className="block text-gray-700 mb-1">Severity</label>
                  <select 
                    name="severity" 
                    value={formData.severity} 
                    onChange={handleInputChange}
                    className="w-full border rounded-md px-3 py-2"
                  >
                    <option>Low</option>
                    <option>Medium</option>
                    <option>High</option>
                  </select>
                </div>
                <div className="mb-4">
                  <label className="block text-gray-700 mb-1">Time & Date Reported</label>
                  <div className="flex gap-2">
                    <input 
                      type="date" 
                      name="date" 
                      value={formData.date} 
                      onChange={handleInputChange}
                      className={`border rounded-md px-3 py-2 flex-1${fieldErrors.includes('date') ? ' border-red-500' : ''}`} 
                      required 
                    />
                    <input 
                      type="time" 
                      name="time" 
                      value={formData.time} 
                      onChange={handleInputChange}
                      className={`border rounded-md px-3 py-2${fieldErrors.includes('time') ? ' border-red-500' : ''}`} 
                      required 
                    />
                  </div>
                </div>
                <div className="mb-4">
                  <label className="block text-gray-700 mb-1">Description</label>
                  <textarea 
                    name="description" 
                    value={formData.description} 
                    onChange={handleInputChange}
                    className={`w-full border rounded-md px-3 py-2 h-32${fieldErrors.includes('description') ? ' border-red-500' : ''}`} 
                    required
                  ></textarea>
                </div>

                <div className="mb-6 border border-gray-200 rounded-md p-4">
                  <div className="mb-4">
                    <h3 className="text-lg font-medium">Attachments</h3>
                    <p className="text-sm text-gray-600">Upload supporting documents, photos, or other relevant files</p>
                  </div>
                  
                  <div className="mb-4">
                    <input
                      type="file"
                      id="file-upload"
                      multiple
                      onChange={handleFileUpload}
                      className={`w-full border rounded-md px-3 py-2${fieldErrors.includes('injuryType') ? ' border-red-500' : ''}`}
                      accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.gif,.txt,.xlsx,.xls"
                    />
                    <div className="mt-2 text-sm text-gray-500">
                      Supported formats: PDF, DOC, DOCX, JPG, PNG, GIF, TXT, XLS, XLSX
                    </div>
                  </div>

                  {attachments.length > 0 && (
                    <div className="space-y-2">
                      <h4 className="font-medium text-gray-700">Selected Files:</h4>
                      {attachments.map((attachment) => (
                        <div key={attachment.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-md border">
                          <div className="flex-1">
                            <div className="font-medium text-sm">{attachment.name}</div>
                            <div className="text-xs text-gray-500">
                              {formatFileSize(attachment.size)} • {attachment.type || 'Unknown type'}
                            </div>
                          </div>
                          <IconButton
                            type="button"
                            onClick={() => removeAttachment(attachment.id)}
                            color="error"
                            size="small"
                          >
                            <RemoveCircleOutline />
                          </IconButton>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Removed WCB Report Required field */}

                {/* Removed WCB Claim Number and Report Submitted Date fields */}

                {/* Removed OHS Report Required field */}

                {/* Removed OHS Report Number and Submitted Date fields */}

                <div className="mb-6 border border-gray-200 rounded-md p-4">
                  <div className="mb-4">
                    <h3 className="text-lg font-medium">Witnesses</h3>
                  </div>
                  <div>
                    {witnesses.map((witness, index) => (
                      <div key={index} className="mb-4 p-3 border border-gray-100 rounded-md bg-gray-50">
                        <div className="flex justify-between items-center mb-2">
                          <h4 className="font-medium">Witness #{index + 1}</h4>
                          {index > 0 && (
                            <IconButton
                              onClick={() => removeWitness(index)}
                              color="error"
                              size="small"
                            >
                              <RemoveCircleOutline />
                            </IconButton>
                          )}
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-3">
                          <TextField
                            label="Name"
                            value={witness.name}
                            onChange={(e) => handleWitnessChange(index, 'name', e.target.value)}
                            fullWidth
                            size="small"
                          />
                          <TextField
                            label="Phone Number"
                            value={witness.phone}
                            onChange={(e) => handleWitnessChange(index, 'phone', e.target.value)}
                            fullWidth
                            size="small"
                          />
                          <TextField
                            label="Email"
                            value={witness.email}
                            onChange={(e) => handleWitnessChange(index, 'email', e.target.value)}
                            fullWidth
                            size="small"
                          />
                        </div>

                        <TextField
                          label="Witness Statement"
                          value={witness.statement}
                          onChange={(e) => handleWitnessChange(index, 'statement', e.target.value)}
                          multiline
                          rows={3}
                          fullWidth
                          size="small"
                        />
                      </div>
                    ))}

                    <div className="flex justify-center mt-2">
                      <Button
                        variant="outlined"
                        startIcon={<AddCircleOutline />}
                        onClick={addWitness}
                      >
                        Add Another Witness
                      </Button>
                    </div>
                  </div>
                </div>

                {/* Render Property/Equipment Damages form beneath the Witnesses section when selected */}
                {(formData.title === 'Property/Equipment Damages' || currentIncidentType === 'Property/Equipment Damages') && (
                  <div data-testid="property-equipment-form" style={{ marginTop: 16 }}>
                    {renderAdditionalForm({ incidentId: currentIncidentId, initialData: additionalFormData, incidentType: 'Property/Equipment Damages' })}
                  </div>
                )}

                {/* Render Vehicle Incident form beneath the Witnesses section when selected */}
                {(formData.title === 'Vehicle Incident' || currentIncidentType === 'Vehicle Incident') && (
                  <div data-testid="vehicle-incident-form" style={{ marginTop: 16 }}>
                    {renderAdditionalForm({ incidentId: currentIncidentId, initialData: additionalFormData, incidentType: 'Vehicle Incident' })}
                  </div>
                )}

                {/* Type-Specific Fields - Integrated into main form */}
                {formData.title === 'First Aid' && (
                  <div className="mb-6 border border-blue-200 rounded-md p-4 bg-blue-50">
                    <h3 className="text-lg font-medium text-blue-800 mb-4">First Aid Incident Details</h3>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-gray-700 mb-1">Type of Injury *</label>
                        <select
                          name="injuryType"
                          value={formData.injuryType}
                          onChange={handleInputChange}
                          className="w-full border rounded-md px-3 py-2"
                          required
                        >
                          <option value="">Select injury type...</option>
                          <option value="Cut/Laceration">Cut/Laceration</option>
                          <option value="Burn">Burn</option>
                          <option value="Sprain/Strain">Sprain/Strain</option>
                          <option value="Bruise">Bruise</option>
                          <option value="Fracture">Fracture</option>
                          <option value="Foreign Object">Foreign Object</option>
                          <option value="Other">Other</option>
                        </select>
                      </div>
                      
                      <div>
                        <label className="block text-gray-700 mb-1">Body Part Affected *</label>
                        <input
                          type="text"
                          name="bodyPartAffected"
                          value={formData.bodyPartAffected}
                          onChange={handleInputChange}
                          className="w-full border rounded-md px-3 py-2"
                          required
                        />
                      </div>
                    </div>
                    
                    <div className="mb-4">
                      <label className="block text-gray-700 mb-1">Treatment Provided *</label>
                      <textarea
                        name="treatmentProvided"
                        value={formData.treatmentProvided}
                        onChange={handleInputChange}
                        className={`w-full border rounded-md px-3 py-2 h-24${fieldErrors.includes('treatmentProvided') ? ' border-red-500' : ''}`}
                        required
                        placeholder="Describe first aid administered"
                      ></textarea>
                    </div>
                    
                    <div>
                      <label className="block text-gray-700 mb-1">Was medical attention beyond first aid required? *</label>
                      <div style={{display: 'flex', flexDirection: 'row', gap: '32px', alignItems: 'center'}}>
                        <label style={{display: 'flex', alignItems: 'center', gap: '8px'}}>
                          <input
                            type="radio"
                            name="medicalAttentionRequired"
                            value="Yes"
                            checked={formData.medicalAttentionRequired === 'Yes'}
                            onChange={handleInputChange}
                            className={`mr-2${fieldErrors.includes('medicalAttentionRequired') ? ' border-red-500' : ''}`}
                          />
                          <span>Yes</span>
                        </label>
                        <label style={{display: 'flex', alignItems: 'center', gap: '8px'}}>
                          <input
                            type="radio"
                            name="medicalAttentionRequired"
                            value="No"
                            checked={formData.medicalAttentionRequired === 'No'}
                            onChange={handleInputChange}
                            className={`mr-2${fieldErrors.includes('medicalAttentionRequired') ? ' border-red-500' : ''}`}
                          />
                          <span>No</span>
                        </label>
                      </div>
                    </div>
                  </div>
                )}

                {formData.title === 'Near Misses' && (
                  <div className="mb-6 border border-blue-200 rounded-md p-4 bg-blue-50">
                    {/* Near Miss Details (additional form moved to appear under Witnesses) */}
                    <h3 className="text-lg font-medium text-blue-800 mb-4">Near Miss Details</h3>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                      <div>
                        <label className="block text-gray-700 mb-1">Near Miss Date</label>
                        <input
                          type="date"
                          name="nearMissDate"
                          value={formData.nearMissDate}
                          onChange={handleInputChange}
                          className="w-full border rounded-md px-3 py-2"
                        />
                      </div>
                      
                      <div>
                        <label className="block text-gray-700 mb-1">Near Miss Time</label>
                        <input
                          type="time"
                          name="nearMissTime"
                          value={formData.nearMissTime}
                          onChange={handleInputChange}
                          className="w-full border rounded-md px-3 py-2"
                        />
                      </div>
                    </div>
                    
                    <div className="mb-4">
                      <label className="block text-gray-700 mb-1">Actions Taken</label>
                      <textarea
                        name="actionsTaken"
                        value={formData.actionsTaken}
                        onChange={handleInputChange}
                        className={`w-full border rounded-md px-3 py-2 h-24${fieldErrors.includes('issueDescription') ? ' border-red-500' : ''}`}
                        placeholder="Describe actions taken"
                      ></textarea>
                    </div>
                    
                    <div>
                      <label className="block text-gray-700 mb-1">Identify the supervisor you reported this incident to</label>
                      <select
                        name="supervisor"
                        value={formData.supervisor}
                        onChange={handleInputChange}
                        className="w-full border rounded-md px-3 py-2"
                      >
                        <option value="">Select an employee</option>
                        {allEmployees.map((employee) => (
                          <option key={employee.id || employee.email} value={employee.email}>
                            {getEmployeeOptionLabel(employee)}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                )}

                {formData.title === 'Work Refusal' && (
                  <div className="mb-6 border border-blue-200 rounded-md p-4 bg-blue-50">
                    <h3 className="text-lg font-medium text-blue-800 mb-4">Work Refusal Details</h3>
                    
                    <div className="mb-4">
                      <label className="block text-gray-700 mb-1">Issue Description *</label>
                      <textarea
                        name="issueDescription"
                        value={formData.issueDescription}
                        onChange={handleInputChange}
                        className="w-full border rounded-md px-3 py-2 h-24"
                        required
                        placeholder="Describe the safety issue"
                      ></textarea>
                    </div>
                    
                    <div className="mb-4">
                      <label className="block text-gray-700 mb-1">Investigation Cause</label>
                      <textarea
                        name="investigationCause"
                        value={formData.investigationCause}
                        onChange={handleInputChange}
                        className="w-full border rounded-md px-3 py-2 h-24"
                        placeholder="Investigation findings"
                      ></textarea>
                    </div>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">                      
                      <div>
                        <label className="block text-gray-700 mb-1">Did you report this to a supervisor?</label>
                        <select
                          name="supervisorName"
                          value={formData.supervisorName}
                          onChange={handleInputChange}
                          className="w-full border rounded-md px-3 py-2"
                        >
                          <option value="">Select an employee</option>
                          {allEmployees.map((employee) => (
                            <option key={employee.id || employee.email} value={employee.email}>
                              {getEmployeeOptionLabel(employee)}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>
                    
                    <div className="mb-4">
                      <label className="block text-gray-700 mb-1">Resolution Steps</label>
                      <textarea
                        name="resolutionSteps"
                        value={formData.resolutionSteps}
                        onChange={handleInputChange}
                        className="w-full border rounded-md px-3 py-2 h-24"
                        placeholder="Steps taken to resolve the issue"
                      ></textarea>
                    </div>
                    
                    <div>
                      <label className="block text-gray-700 mb-1">Hazard Mitigated?</label>
                      <div style={{display: 'flex', flexDirection: 'row', gap: '32px', alignItems: 'center'}}>
                        <label style={{display: 'flex', alignItems: 'center', gap: '8px'}}>
                          <input
                            type="radio"
                            name="hazardMitigated"
                            value="Yes"
                            checked={formData.hazardMitigated === 'Yes'}
                            onChange={handleInputChange}
                            className="mr-2"
                          />
                          <span>Yes</span>
                        </label>
                        <label style={{display: 'flex', alignItems: 'center', gap: '8px'}}>
                          <input
                            type="radio"
                            name="hazardMitigated"
                            value="No"
                            checked={formData.hazardMitigated === 'No'}
                            onChange={handleInputChange}
                            className="mr-2"
                          />
                          <span>No</span>
                        </label>
                      </div>
                    </div>
                  </div>
                )}

                {formData.title === 'Vandalism' && (
                  <div className="mb-6 border border-blue-200 rounded-md p-4 bg-blue-50">
                    <h3 className="text-lg font-medium text-blue-800 mb-4">Vandalism Details</h3>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                      <div>
                        <label className="block text-gray-700 mb-1">Damage Type</label>
                        <input
                          type="text"
                          name="damageType"
                          value={formData.damageType}
                          onChange={handleInputChange}
                          className="w-full border rounded-md px-3 py-2"
                          placeholder="Type of damage"
                        />
                      </div>
                      
                      <div>
                        <label className="block text-gray-700 mb-1">Estimated Cost</label>
                        <input
                          type="text"
                          name="estimatedCost"
                          value={formData.estimatedCost}
                          onChange={handleInputChange}
                          className="w-full border rounded-md px-3 py-2"
                          placeholder="$0.00"
                        />
                      </div>
                    </div>
                    
                    <div className="mb-4">
                      <label className="block text-gray-700 mb-1">Items Damaged</label>
                      <textarea
                        name="itemsDamaged"
                        value={formData.itemsDamaged}
                        onChange={handleInputChange}
                        className="w-full border rounded-md px-3 py-2 h-24"
                        placeholder="List damaged items"
                      ></textarea>
                    </div>
                    
                    <div className="mb-4">
                      <label className="block text-gray-700 mb-1">Suspected Perpetrator</label>
                      <input
                        type="text"
                        name="suspectedPerpetrator"
                        value={formData.suspectedPerpetrator}
                        onChange={handleInputChange}
                        className="w-full border rounded-md px-3 py-2"
                        placeholder="If known"
                      />
                    </div>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-gray-700 mb-1">Reported to Police?</label>
                        <div style={{display: 'flex', flexDirection: 'row', gap: '32px', alignItems: 'center'}}>
                          <label style={{display: 'flex', alignItems: 'center', gap: '8px'}}>
                            <input
                              type="radio"
                              name="reportedToPolice"
                              value="Yes"
                              checked={formData.reportedToPolice === 'Yes'}
                              onChange={handleInputChange}
                            />
                            <span>Yes</span>
                          </label>
                          <label style={{display: 'flex', alignItems: 'center', gap: '8px'}}>
                            <input
                              type="radio"
                              name="reportedToPolice"
                              value="No"
                              checked={formData.reportedToPolice === 'No'}
                              onChange={handleInputChange}
                            />
                            <span>No</span>
                          </label>
                        </div>
                      </div>
                      
                      <div>
                        <label className="block text-gray-700 mb-1">Police Report Number</label>
                        <input
                          type="text"
                          name="policeReportNumber"
                          value={formData.policeReportNumber}
                          onChange={handleInputChange}
                          className="w-full border rounded-md px-3 py-2"
                          placeholder="If applicable"
                        />
                      </div>
                    </div>
                  </div>
                )}

                {/* Render the previously-missing type-specific sections inline so they show immediately when selected */}
                {formData.title === 'Injuries Lost-time' && (
                  <div className="mb-6 border border-blue-200 rounded-md p-4 bg-blue-50">
                    <h3 className="text-lg font-medium text-blue-800 mb-4">Injuries - Lost Time</h3>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                      <div>
                        <label className="block text-gray-700 mb-1">Employee Name</label>
                        <input
                          type="text"
                          name="employeeName"
                          value={formData.employeeName || ''}
                          onChange={handleInputChange}
                          className="w-full border rounded-md px-3 py-2"
                        />
                      </div>
                      <div>
                        <label className="block text-gray-700 mb-1">Date of Injury</label>
                        <input
                          type="date"
                          name="dateOfInjury"
                          value={formData.dateOfInjury || ''}
                          onChange={handleInputChange}
                          className="w-full border rounded-md px-3 py-2"
                        />
                      </div>
                    </div>

                    <div className="mb-4">
                      <label className="block text-gray-700 mb-1">Hours / Days Lost</label>
                      <input
                        type="number"
                        name="hoursLost"
                        value={formData.hoursLost || formData.lostDays || 0}
                        onChange={handleInputChange}
                        className="w-full border rounded-md px-3 py-2"
                        min="0"
                      />
                    </div>

                    <div className="mb-4">
                      <label className="block text-gray-700 mb-1">Description</label>
                      <textarea
                        name="description"
                        value={formData.description || ''}
                        onChange={handleInputChange}
                        className="w-full border rounded-md px-3 py-2 h-24"
                      />
                    </div>
                  </div>
                )}

                {formData.title === 'Injuries Non-lost time' && (
                  <div className="mb-6 border border-blue-200 rounded-md p-4 bg-blue-50">
                    <h3 className="text-lg font-medium text-blue-800 mb-4">Injuries - Non Lost Time</h3>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                      <div>
                        <label className="block text-gray-700 mb-1">Employee Name</label>
                        <input
                          type="text"
                          name="employeeName"
                          value={formData.employeeName || ''}
                          onChange={handleInputChange}
                          className="w-full border rounded-md px-3 py-2"
                        />
                      </div>
                      <div>
                        <label className="block text-gray-700 mb-1">Date of Injury</label>
                        <input
                          type="date"
                          name="dateOfInjury"
                          value={formData.dateOfInjury || ''}
                          onChange={handleInputChange}
                          className="w-full border rounded-md px-3 py-2"
                        />
                      </div>
                    </div>

                    <div className="mb-4">
                      <label className="block text-gray-700 mb-1">Description</label>
                      <textarea
                        name="description"
                        value={formData.description || ''}
                        onChange={handleInputChange}
                        className="w-full border rounded-md px-3 py-2 h-24"
                      />
                    </div>
                  </div>
                )}

                {formData.title === 'Hospital Admittance' && (
                  <div className="mb-6 border border-blue-200 rounded-md p-4 bg-blue-50">
                    <h3 className="text-lg font-medium text-blue-800 mb-4">Hospital Admittance</h3>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                      <div>
                        <label className="block text-gray-700 mb-1">Patient Name</label>
                        <input
                          type="text"
                          name="patientName"
                          value={formData.patientName || ''}
                          onChange={handleInputChange}
                          className="w-full border rounded-md px-3 py-2"
                        />
                      </div>
                      <div>
                        <label className="block text-gray-700 mb-1">Date of Birth</label>
                        <input
                          type="date"
                          name="dateOfBirth"
                          value={formData.dateOfBirth || ''}
                          onChange={handleInputChange}
                          className="w-full border rounded-md px-3 py-2"
                        />
                      </div>
                    </div>

                    <div className="mb-4">
                      <label className="block text-gray-700 mb-1">Admission Date</label>
                      <input
                        type="date"
                        name="admissionDate"
                        value={formData.admissionDate || formData.date || ''}
                        onChange={handleInputChange}
                        className="w-full border rounded-md px-3 py-2"
                      />
                    </div>

                    <div className="mb-4">
                      <label className="block text-gray-700 mb-1">Reason / Notes</label>
                      <textarea
                        name="reason"
                        value={formData.reason || formData.description || ''}
                        onChange={handleInputChange}
                        className="w-full border rounded-md px-3 py-2 h-24"
                      />
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-gray-700 mb-1">Attending Doctor</label>
                        <input
                          type="text"
                          name="doctor"
                          value={formData.doctor || ''}
                          onChange={handleInputChange}
                          className="w-full border rounded-md px-3 py-2"
                        />
                      </div>
                      <div>
                        <label className="block text-gray-700 mb-1">Additional Notes</label>
                        <input
                          type="text"
                          name="notes"
                          value={formData.notes || ''}
                          onChange={handleInputChange}
                          className="w-full border rounded-md px-3 py-2"
                        />
                      </div>
                    </div>
                  </div>
                )}

                {formData.title === 'Fatalities' && (
                  <div className="mb-6 border border-blue-200 rounded-md p-4 bg-blue-50">
                    <h3 className="text-lg font-medium text-blue-800 mb-4">Fatality Details</h3>
                    <div className="mb-4">
                      <label className="block text-gray-700 mb-1">Name</label>
                      <input
                        type="text"
                        name="name"
                        value={formData.name || ''}
                        onChange={handleInputChange}
                        className="w-full border rounded-md px-3 py-2"
                      />
                    </div>
                    <div className="mb-4">
                      <label className="block text-gray-700 mb-1">Cause</label>
                      <input
                        type="text"
                        name="cause"
                        value={formData.cause || ''}
                        onChange={handleInputChange}
                        className="w-full border rounded-md px-3 py-2"
                      />
                    </div>
                    <div className="mb-4">
                      <label className="block text-gray-700 mb-1">Date</label>
                      <input
                        type="date"
                        name="date"
                        value={formData.date || ''}
                        onChange={handleInputChange}
                        className="w-full border rounded-md px-3 py-2"
                      />
                    </div>
                  </div>
                )}

                {formData.title === 'OHS Serious' && (
                  <div className="mb-6 border border-blue-200 rounded-md p-4 bg-blue-50">
                    <h3 className="text-lg font-medium text-blue-800 mb-4">OHS Serious Incident Details</h3>
                    <div className="mb-4">
                      <label className="block text-gray-700 mb-1">Details / File Number</label>
                      <textarea
                        name="details"
                        value={formData.details || ''}
                        onChange={handleInputChange}
                        className="w-full border rounded-md px-3 py-2 h-24"
                      />
                    </div>
                  </div>
                )}

                {formData.title === 'OHS Potential Serious Incident' && (
                  <div className="mb-6 border border-blue-200 rounded-md p-4 bg-blue-50">
                    <h3 className="text-lg font-medium text-blue-800 mb-4">OHS Potential Serious Incident Details</h3>
                    <div className="mb-4">
                      <label className="block text-gray-700 mb-1">Details</label>
                      <textarea
                        name="details"
                        value={formData.details || ''}
                        onChange={handleInputChange}
                        className="w-full border rounded-md px-3 py-2 h-24"
                      />
                    </div>
                  </div>
                )}

                {/* Save as Draft button and Submit button */}
                {/* Inline Incident Report Acknowledgment section, shown at the bottom when incident type is chosen */}
                {formData.title && (
                  <div className="mb-6 p-4 border border-blue-200 rounded-md bg-blue-50">
                    <h3 className="text-lg font-semibold mb-2 text-blue-800">Incident Report Acknowledgment</h3>
                    <p className="text-gray-700 mb-4">Please read and acknowledge the following statements before submitting your incident report:</p>
                    <div className="mb-2" style={{display: 'flex', flexDirection: 'row', gap: '32px', alignItems: 'center', flexWrap: 'wrap'}}>
                      <div style={{display: 'flex', alignItems: 'center', gap: '8px'}}>
                        <span className="text-sm text-gray-700" style={{whiteSpace: 'nowrap'}}>
                          I confirm the information is accurate and complete.
                        </span>
                        <input
                          type="checkbox"
                          checked={acknowledgmentChecks.accuracy}
                          onChange={() => handleAcknowledgmentChange('accuracy')}
                          style={{minWidth: '24px'}}
                          className="w-5 h-5 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                        />
                      </div>
                      <div style={{display: 'flex', alignItems: 'center', gap: '8px'}}>
                        <span className="text-sm text-gray-700" style={{whiteSpace: 'nowrap'}}>
                          All relevant details and witnesses are included.
                        </span>
                        <input
                          type="checkbox"
                          checked={acknowledgmentChecks.complete}
                          onChange={() => handleAcknowledgmentChange('complete')}
                          style={{minWidth: '24px'}}
                          className="w-5 h-5 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                        />
                      </div>
                      <div style={{display: 'flex', alignItems: 'center', gap: '8px'}}>
                        <span className="text-sm text-gray-700" style={{whiteSpace: 'nowrap'}}>
                          I am authorized to submit this report.
                        </span>
                        <input
                          type="checkbox"
                          checked={acknowledgmentChecks.authorized}
                          onChange={() => handleAcknowledgmentChange('authorized')}
                          style={{minWidth: '24px'}}
                          className="w-5 h-5 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                        />
                      </div>
                      <div style={{display: 'flex', alignItems: 'center', gap: '8px'}}>
                        <span className="text-sm text-gray-700" style={{whiteSpace: 'nowrap'}}>
                          I understand this report is for investigation and compliance.
                        </span>
                        <input
                          type="checkbox"
                          checked={acknowledgmentChecks.understand}
                          onChange={() => handleAcknowledgmentChange('understand')}
                          style={{minWidth: '24px'}}
                          className="w-5 h-5 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                        />
                      </div>
                    </div>
                    <div className="flex justify-end mt-4">
                      <button
                        type="button"
                        className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700"
                        disabled={!Object.values(acknowledgmentChecks).every(checked => checked) || mainFormLoading}
                        onClick={handleAcknowledgmentSubmit}
                      >
                        {mainFormLoading ? "Submitting..." : "Acknowledge & Submit Report"}
                      </button>
                    </div>
                  </div>
                )}
                <div className="flex justify-between">
                  <button 
                    type="button"
                    onClick={saveDraft}
                    className="bg-gray-600 text-white px-4 py-2 rounded-lg hover:bg-gray-700"
                  >
                    Save as Draft
                  </button>
                  <button 
                    type="button"
                    onClick={resetForm}
                    className="bg-gray-300 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-400 mr-2"
                    disabled={mainFormLoading}
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          ) : (
            <>
              <div className="stats-container">
                <div className="stat">
                  <h3 className="text-gray-500 text-sm">Open Incidents</h3>
                  <p className="text-3xl font-bold">{openIncidents}</p>
                </div>
                <div className="stat">
                  <h3 className="text-gray-500 text-sm">Resolved</h3>
                  <p className="text-3xl font-bold">{resolvedIncidents}</p>
                </div>
                <div className="stat">
                  <h3 className="text-gray-500 text-sm">High Severity</h3>
                  <p className="text-3xl font-bold">{highSeverity}</p>
                </div>
              </div>
            </>
          )}

        </main>

      </div>
    </div>
  );
};

export default React.memo(IncidentReportingDashboard);